import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ShoppingBagIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { PageSizeSelect } from '../../components/ui/PageSizeSelect';
import { usePageSize, ALL_PAGE_SIZE } from '../../hooks/usePageSize';
import {
  FULFILLMENT_LABELS,
  FULFILLMENT_COLORS,
  ALL_FULFILLMENT_STATUSES,
  formatMoney,
  type HardwareOrder,
  type HardwareOrderFulfillmentStatus,
} from '../../types/hardwareOrders';

type StatusFilter = HardwareOrderFulfillmentStatus | 'all';

const FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  ...ALL_FULFILLMENT_STATUSES.map((s) => ({ key: s, label: FULFILLMENT_LABELS[s] })),
];

// Hardware-order volume is small (tens per month), so the whole list is
// fetched once and searched / filtered / sorted / paged client-side.
const FETCH_PAGE_LIMIT = 200;

const PAGE_SIZES = [25, 50, 100, ALL_PAGE_SIZE] as const;

type SortField = 'order' | 'date' | 'customer' | 'items' | 'total' | 'payment' | 'discount' | 'fulfillment';

function unmatchedCount(order: HardwareOrder): number {
  return (order.items ?? []).filter((it) => it.sku && !it.product_id).length;
}

// Discount codes live in raw_payload — GraphQL backfill shape (discountCodes:
// string[]) or webhook REST shape (discount_codes: [{code}]).
function discountCodes(order: HardwareOrder): string[] {
  const p = order.raw_payload as any;
  const raw = p?.discountCodes ?? (p?.discount_codes ?? []).map((d: any) => d?.code);
  return (Array.isArray(raw) ? raw : []).filter(
    (c: unknown): c is string => typeof c === 'string' && c.trim() !== '',
  );
}

interface OrderRow {
  order: HardwareOrder;
  codes: string[];
  itemCount: number;
  unmatched: number;
  orderNo: number;
  haystack: string;
}

// One lowercase blob per order so free-text search can match anything visible
// (plus contact detail from the payload).
function buildHaystack(order: HardwareOrder, codes: string[]): string {
  const p = order.raw_payload as any;
  const ship = p?.shippingAddress ?? p?.shipping_address ?? {};
  return [
    order.shopify_order_number,
    order.shopify_order_id,
    order.email,
    order.financial_status,
    FULFILLMENT_LABELS[order.fulfillment_status],
    ...codes,
    ...(order.items ?? []).flatMap((it) => [it.title, it.sku]),
    formatMoney(order.total_cents, order.currency),
    ship?.name,
    ship?.company,
    ship?.address1,
    ship?.city,
    ship?.province,
    ship?.zip,
    ship?.country,
    ship?.phone,
    p?.phone,
    p?.customer?.phone,
    p?.customer?.displayName,
    new Date(order.created_at).toLocaleDateString('en-NZ'),
  ]
    .filter((x): x is string => typeof x === 'string' && x !== '')
    .join(' ')
    .toLowerCase();
}

function SortHeader({
  field,
  label,
  sortField,
  sortDirection,
  onSort,
  align = 'left',
}: {
  field: SortField;
  label: string;
  sortField: SortField;
  sortDirection: 'asc' | 'desc';
  onSort: (field: SortField) => void;
  align?: 'left' | 'right';
}) {
  const active = sortField === field;
  const Icon = !active ? ChevronUpDownIcon : sortDirection === 'asc' ? ChevronUpIcon : ChevronDownIcon;
  return (
    <th className={`px-4 py-3 font-medium ${align === 'right' ? 'text-right' : ''}`}>
      <button
        onClick={() => onSort(field)}
        className={`inline-flex items-center gap-1 uppercase tracking-wide hover:text-slate-300 ${
          active ? 'text-cyan-400' : ''
        }`}
      >
        {label}
        <Icon className="w-3.5 h-3.5" />
      </button>
    </th>
  );
}

export function AdminHardwareOrdersPage() {
  const [status, setStatus] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [discountFilter, setDiscountFilter] = useState('');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize('hardware-orders');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, status, discountFilter, pageSize, sortField, sortDirection]);

  const { data: allOrders, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['admin', 'hardware-orders', 'all'],
    queryFn: async () => {
      const first = await api.adminListHardwareOrders({ page: 1, limit: FETCH_PAGE_LIMIT });
      const orders = [...first.orders];
      for (let p = 2; p <= first.pagination.totalPages; p++) {
        const next = await api.adminListHardwareOrders({ page: p, limit: FETCH_PAGE_LIMIT });
        orders.push(...next.orders);
      }
      return orders;
    },
  });

  const rows: OrderRow[] = useMemo(
    () =>
      (allOrders ?? []).map((order) => {
        const codes = discountCodes(order);
        return {
          order,
          codes,
          itemCount: (order.items ?? []).reduce((n, it) => n + it.quantity, 0),
          unmatched: unmatchedCount(order),
          orderNo: Number((order.shopify_order_number ?? '').replace(/\D/g, '')) || 0,
          haystack: buildHaystack(order, codes),
        };
      }),
    [allOrders],
  );

  const allCodes = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows) for (const c of r.codes) counts.set(c, (counts.get(c) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows]);

  const filtered = useMemo(() => {
    const tokens = debouncedSearch.toLowerCase().split(/\s+/).filter(Boolean);
    const result = rows.filter((r) => {
      if (status !== 'all' && r.order.fulfillment_status !== status) return false;
      if (discountFilter && !r.codes.includes(discountFilter)) return false;
      return tokens.every((t) => r.haystack.includes(t));
    });

    const dir = sortDirection === 'asc' ? 1 : -1;
    const cmp = (a: OrderRow, b: OrderRow): number => {
      switch (sortField) {
        case 'order':
          return a.orderNo - b.orderNo;
        case 'date':
          return a.order.created_at.localeCompare(b.order.created_at);
        case 'customer':
          return (a.order.email ?? '').localeCompare(b.order.email ?? '');
        case 'items':
          return a.itemCount - b.itemCount;
        case 'total':
          return a.order.total_cents - b.order.total_cents;
        case 'payment':
          return (a.order.financial_status ?? '').localeCompare(b.order.financial_status ?? '');
        case 'discount':
          return a.codes.join(',').localeCompare(b.codes.join(','));
        case 'fulfillment':
          return a.order.fulfillment_status.localeCompare(b.order.fulfillment_status);
      }
    };
    return result.sort((a, b) => dir * cmp(a, b));
  }, [rows, status, discountFilter, debouncedSearch, sortField, sortDirection]);

  const totalPages = pageSize >= ALL_PAGE_SIZE ? 1 : Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = pageSize >= ALL_PAGE_SIZE ? filtered : filtered.slice((page - 1) * pageSize, page * pageSize);
  const grossCents = filtered.reduce((n, r) => n + r.order.total_cents, 0);
  const currency = filtered[0]?.order.currency ?? 'NZD';

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection(field === 'date' || field === 'total' || field === 'items' ? 'desc' : 'asc');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <ShoppingBagIcon className="w-6 h-6 text-cyan-400" />
            Hardware Orders
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Orders ingested from Shopify. Awaiting orders are fulfilled via the inventory allocate / build / ship flow.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:border-slate-600"
        >
          <ArrowPathIcon className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Filters + search */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setStatus(f.key)}
              className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                status === f.key
                  ? 'border-cyan-500/50 bg-cyan-500/10 text-cyan-300'
                  : 'border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-600'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 flex-wrap ml-auto">
          <div className="relative">
            <MagnifyingGlassIcon className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search orders…"
              className="pl-9 pr-3 py-1.5 w-56 bg-slate-800/50 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
            />
          </div>

          <select
            value={discountFilter}
            onChange={(e) => setDiscountFilter(e.target.value)}
            className="px-2 py-1.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
            title="Filter by discount code"
          >
            <option value="">All discount codes</option>
            {allCodes.map(([code, count]) => (
              <option key={code} value={code}>
                {code} ({count})
              </option>
            ))}
          </select>

          <PageSizeSelect value={pageSize} onChange={setPageSize} options={PAGE_SIZES} />
        </div>
      </div>

      <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-slate-400">Loading…</div>
        ) : isError ? (
          <div className="p-8 text-center text-red-400">Failed to load hardware orders.</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-slate-400">No hardware orders found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-700">
                  <SortHeader field="order" label="Order" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                  <SortHeader field="date" label="Date" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                  <SortHeader field="customer" label="Customer" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                  <SortHeader field="items" label="Items" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                  <SortHeader field="total" label="Total" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} align="right" />
                  <SortHeader field="payment" label="Payment" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                  <SortHeader field="discount" label="Discount" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                  <SortHeader field="fulfillment" label="Fulfillment" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {pageRows.map(({ order, codes, itemCount, unmatched }) => (
                  <tr key={order.id} className="hover:bg-slate-700/30">
                    <td className="px-4 py-3">
                      <Link to={`/admin/hardware-orders/${order.id}`} className="text-cyan-400 hover:text-cyan-300 font-medium">
                        {order.shopify_order_number ?? order.shopify_order_id}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-slate-400">{new Date(order.created_at).toLocaleDateString('en-NZ')}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {order.user_id && <UserIcon className="w-3.5 h-3.5 text-green-400" title="Linked to a platform user" />}
                        <span className="text-slate-300">{order.email ?? '—'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-400">
                      <div className="flex items-center gap-2">
                        <span>{itemCount}</span>
                        {unmatched > 0 && (
                          <span
                            className="inline-flex items-center gap-1 text-amber-400"
                            title={`${unmatched} line item(s) with an unmatched SKU`}
                          >
                            <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                            {unmatched}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-300 font-mono">{formatMoney(order.total_cents, order.currency)}</td>
                    <td className="px-4 py-3 text-slate-400">{order.financial_status?.replace(/_/g, ' ') ?? '—'}</td>
                    <td className="px-4 py-3">
                      {codes.length > 0 ? (
                        <div className="flex items-center gap-1 flex-wrap">
                          {codes.map((code) => (
                            <button
                              key={code}
                              onClick={() => setDiscountFilter(code)}
                              className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 text-xs font-mono hover:bg-emerald-500/20"
                              title={`Filter by ${code}`}
                            >
                              {code}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${FULFILLMENT_COLORS[order.fulfillment_status]}`}>
                        {FULFILLMENT_LABELS[order.fulfillment_status]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Result summary + pagination */}
      {!isLoading && !isError && (
        <div className="flex items-center justify-between flex-wrap gap-3 text-sm text-slate-400">
          <span>
            {filtered.length} order{filtered.length === 1 ? '' : 's'}
            {filtered.length !== rows.length && ` (of ${rows.length})`} ·{' '}
            <span className="font-mono">{formatMoney(grossCents, currency)}</span> gross
            {discountFilter && <span className="text-emerald-300"> · code {discountFilter}</span>}
          </span>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-700 disabled:opacity-40 hover:border-slate-600"
              >
                <ChevronLeftIcon className="w-4 h-4" /> Prev
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-700 disabled:opacity-40 hover:border-slate-600"
              >
                Next <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
